import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  CalendarMonth,
  Schedule,
  LocationOn,
  CheckCircle,
  Cancel,
  Key,
  HourglassEmpty
} from '@mui/icons-material';
import { User } from 'firebase/auth';
import { getUserBookings, getAllStations, getAllStationTypes, Booking, Station, StationType } from '../Firebase/firebaseApi.ts';
import { trackEvent } from '../Analytics/posthog.ts';
import BackgroundWrapper from '../components/BackgroundWrapper.tsx';
import './MyBookings.css';

interface MyBookingsProps {
  user: User | null;
}

interface BookingWithDetails extends Booking {
  station?: Station;
  stationType?: StationType;
}

const MyBookings: React.FC<MyBookingsProps> = ({ user }) => {
  const [bookings, setBookings] = useState<BookingWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserBookings = async () => {
      if (!user?.email) {
        setLoading(false);
        return;
      }

      // Track page visit
      trackEvent('my_bookings_page_viewed', {
        user_email: user.email,
      });

      try {
        setLoading(true);
        setError(null);

        const [userBookings, stations, stationTypes] = await Promise.all([
          getUserBookings(user.email),
          getAllStations(),
          getAllStationTypes()
        ]);

        // Enrich bookings with station and station type details
        const enrichedBookings: BookingWithDetails[] = userBookings.map(booking => {
          const station = stations.find(s => s.id === booking.stationId);
          const stationType = station ? stationTypes.find(st => st.id === station.typeId) : undefined;
          
          return {
            ...booking,
            station,
            stationType
          };
        });

        // Sort by booking date (newest first)
        enrichedBookings.sort((a, b) => 
          new Date(b.fromDate).getTime() - new Date(a.fromDate).getTime()
        );

        setBookings(enrichedBookings);
      } catch (err) {
        console.error('Error fetching user bookings:', err);
        setError('שגיאה בטעינת ההזמנות');
      } finally {
        setLoading(false);
      }
    };

    fetchUserBookings();
  }, [user]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const groupSizeById = bookings.reduce<Record<string, number>>((acc, b) => {
    if (!b.groupId) return acc;
    acc[b.groupId] = (acc[b.groupId] ?? 0) + 1;
    return acc;
  }, {});

  const getStatusChip = (booking: Booking) => {
    if (!booking.approved) {
      return (
        <Chip
          icon={<HourglassEmpty />}
          label="ממתינה לאישור"
          color="warning"
          variant="outlined"
          size="small"
        />
      );
    }

    const now = new Date();
    const fromDate = new Date(booking.fromDate);
    const toDate = new Date(booking.toDate);

    if (now > toDate) {
      return (
        <Chip
          icon={<CheckCircle />}
          label="הושלם"
          color="success"
          variant="outlined"
          size="small"
        />
      );
    }

    if (now >= fromDate && now <= toDate) {
      return (
        <Chip
          icon={<Schedule />}
          label="פעיל כעת"
          color="primary"
          variant="filled"
          size="small"
        />
      );
    }

    return (
      <Chip
        icon={<CalendarMonth />}
        label="מתוכנן"
        color="info"
        variant="outlined"
        size="small"
      />
    );
  };

  if (!user) {
    return (
      <BackgroundWrapper>
        <Container maxWidth="lg" sx={{ py: 8, direction: 'rtl' }}>
          <Alert severity="warning" sx={{ textAlign: 'right' }}>
            יש להתחבר כדי לצפות בהזמנות
          </Alert>
        </Container>
      </BackgroundWrapper>
    );
  }

  if (loading) {
    return (
      <BackgroundWrapper>
        <Container maxWidth="lg" sx={{ py: 8, direction: 'rtl' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress sx={{ color: 'white' }} />
            <Typography variant="h6" sx={{ ml: 2, color: 'white', textShadow: "2px 2px 4px rgba(0,0,0,0.7)" }}>
              טוען הזמנות...
            </Typography>
          </Box>
        </Container>
      </BackgroundWrapper>
    );
  }

  return (
    <BackgroundWrapper>
      <Container maxWidth="lg" className="mybookings-container" sx={{ py: 8, direction: 'rtl' }}>
        <Typography variant="h3" component="h1" sx={{ mb: 4, textAlign: 'center', color: 'white', fontWeight: 'bold', textShadow: "2px 2px 4px rgba(0,0,0,0.7)" }}>
          ההזמנות שלי
        </Typography>

        <Typography variant="h6" sx={{ mb: 4, textAlign: 'center', color: 'white', textShadow: "1px 1px 2px rgba(0,0,0,0.7)" }}>
          כל ההזמנות שלך במקום אחד
        </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 4, textAlign: 'right' }}>
          {error}
        </Alert>
      )}

      {bookings.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Cancel sx={{ fontSize: 80, color: 'white', mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 2, color: 'white', textShadow: "2px 2px 4px rgba(0,0,0,0.7)" }}>
            אין הזמנות
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', textShadow: "1px 1px 2px rgba(0,0,0,0.7)" }}>
            עדיין לא ביצעת הזמנות במערכת
          </Typography>
        </Box>
      ) : (
        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              md: 'repeat(2, 1fr)', 
              lg: 'repeat(3, 1fr)' 
            }, 
            gap: 3 
          }}
        >
          {bookings.map((booking) => (
            <Card key={booking.id} 
                elevation={2} 
                sx={{ 
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                      {booking.stationType?.typeFriendlyName || 'סוג לא ידוע'}
                    </Typography>
                    {getStatusChip(booking)}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      עמדה: {booking.station?.id || booking.stationId}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CalendarMonth sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>מתאריך:</strong> {formatDate(booking.fromDate)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Schedule sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>שעה:</strong> {formatTime(booking.fromDate)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CalendarMonth sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>עד תאריך:</strong> {formatDate(booking.toDate)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Schedule sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>שעה:</strong> {formatTime(booking.toDate)}
                    </Typography>
                  </Box>

                  {booking.approved && booking.keypadCode && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Key sx={{ mr: 1, color: 'success.main', fontSize: 18 }} />
                        <Typography variant="body2" fontWeight="bold">
                          קוד כניסה: {booking.keypadCode}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {booking.approved && !booking.keypadCode && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        קוד הכניסה יישלח במייל
                      </Typography>
                    </Box>
                  )}

                  {booking.station?.tickchakLink && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        קישור תיקצ'ק זמין
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
          ))}
        </Box>
      )}
      </Container>
    </BackgroundWrapper>
  );
};

export default MyBookings;
